// Holds the signed-in user's account socket open on every page, independent of
// rooms, and applies confirmed Loon balances to the auth user. Consumers
// (celebrations, world objects) subscribe to its messages.

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ACCOUNT_SOCKET_NO_SESSION,
  AccountServerMessage,
  AccountServerMessageType,
  ConnectionState,
} from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';
import { useAuth } from '../hooks/useAuth';
import { AccountSocketClient } from '../transport/websocket/accountSocketClient';

const logger = createLogger('AccountSocketContext');

export interface AccountSocketContextType {
  status: ConnectionState;
  /** Returns an unsubscribe function. */
  subscribe: (handler: (message: AccountServerMessage) => void) => () => void;
}

export const AccountSocketContext =
  createContext<AccountSocketContextType | null>(null);

export const AccountSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, setUser, refetchUser } = useAuth();
  const userId = user?._id;
  const [client] = useState(() => new AccountSocketClient());
  const [status, setStatus] = useState(ConnectionState.DISCONNECTED);

  // setUser and refetchUser identities are not guaranteed stable; the socket
  // lifecycle must depend on the user id alone.
  const authRef = useRef({ setUser, refetchUser });
  authRef.current = { setUser, refetchUser };

  useEffect(() => {
    if (!userId) {
      setStatus(ConnectionState.DISCONNECTED);
      return;
    }
    // After a no-session close whose session re-check still finds this user,
    // reconnect once; a second one in a row stops, rather than looping.
    let sessionRechecked = false;

    const offMessage = client.on('message', (message) => {
      const { setUser, refetchUser } = authRef.current;
      switch (message.type) {
        case AccountServerMessageType.SNAPSHOT: {
          sessionRechecked = false;
          if (message.payload.userId !== userId) {
            // Another tab signed in as someone else and replaced the cookie
            void refetchUser();
            return;
          }
          const { loonBalance } = message.payload;
          setUser((prev) =>
            prev && prev._id === userId ? { ...prev, loonBalance } : prev
          );
          return;
        }
        case AccountServerMessageType.LOON_BALANCE: {
          const { loonBalance } = message.payload;
          setUser((prev) =>
            prev && prev._id === userId ? { ...prev, loonBalance } : prev
          );
          return;
        }
      }
    });

    const offState = client.on('connectionState', ({ state, code }) => {
      setStatus(state);
      // The close itself carries the code; the socket then stops retrying.
      if (
        state !== ConnectionState.DISCONNECTED ||
        code !== ACCOUNT_SOCKET_NO_SESSION
      ) {
        return;
      }
      // Signed out elsewhere, expired, or replaced. A missing session clears
      // the user; a different one switches it and this effect reconnects.
      void authRef.current.refetchUser().then((fresh) => {
        if (fresh?._id !== userId) return;
        if (sessionRechecked) {
          logger.warn('Account socket refused a session the API accepts');
          return;
        }
        sessionRechecked = true;
        client.connect();
      });
    });

    client.connect();
    return () => {
      offMessage();
      offState();
      client.disconnect();
    };
  }, [client, userId]);

  const subscribe = useCallback<AccountSocketContextType['subscribe']>(
    (handler) => client.on('message', handler),
    [client]
  );

  const value = useMemo(() => ({ status, subscribe }), [status, subscribe]);

  return (
    <AccountSocketContext.Provider value={value}>
      {children}
    </AccountSocketContext.Provider>
  );
};
